package fr.manyverse.app.domain.usecase

import fr.manyverse.app.domain.model.ActiveCombat
import fr.manyverse.app.domain.model.CombatParticipant
import fr.manyverse.app.domain.repository.CombatRepository

/**
 * Met à jour la liste ordonnée des participants pendant un combat
 * (réordonnancement ou suppression).
 *
 * L'`activeIndex` est recalculé pour continuer à pointer le même participant
 * (par son id). Si le participant actif a été retiré, on retombe sur la même
 * position dans la nouvelle liste (le suivant prend la main), bornée à la taille.
 *
 * Les cibles déjà connues (`combat.targetParticipants`) sont transmises au repository
 * pour qu'un joueur dont le personnage est retiré reste ciblé (Realtime + RLS) jusqu'à
 * l'arrêt du combat : sa bulle de combat ne doit pas se fermer tant que le combat dure.
 */
class UpdateCombatParticipantsUseCase(
    private val combatRepository: CombatRepository
) {
    suspend operator fun invoke(
        combat: ActiveCombat,
        newParticipants: List<CombatParticipant>
    ): Result<Unit> {
        if (newParticipants.isEmpty()) {
            // Plus de participants : on arrête le combat.
            return combatRepository.stopCombat(combat.id)
        }

        val activeId = combat.participants.getOrNull(combat.activeIndex)?.id
        val keptIndex = newParticipants.indexOfFirst { it.id == activeId }
        val newActiveIndex = when {
            keptIndex >= 0 -> keptIndex
            else -> combat.activeIndex.coerceIn(0, newParticipants.lastIndex)
        }

        return combatRepository.updateParticipants(
            combat.id,
            newParticipants,
            newActiveIndex,
            combat.targetParticipants
        )
    }
}
